import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Button, Stack, Typography } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import useUnitProvider from '/@/core/context/unit';
import { useFieldById } from '/@/hooks/fields';
import { useHasWriteAccess } from '/@/hooks/session';
import { useToggle } from '/@/hooks/toggle/useToggle';
import { _ } from '/@/locale';
import { getAppInstance } from '/@/store/session/selectors';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import { formatDate } from '/@/utils';
import { getCropMonitoringData } from '/@/utils/crop-monitoring';
import CreateReportModal from '/@/views/fields/field-monitoring/components/create-report-modal';
import ModalV2 from '../../bayer/modal-v2';
import colors from '../../theme-provider/colors';
import CropMonitoringDetailCard from '../detail-card';
import HarvestLabelAndModal from '../harvest-alert';

const HarvestPreparationCard = ({
    cropMonitoring,
    setYieldEstimationDrawerIsOpen,
    showHarvestRequest,
}: {
    cropMonitoring: CropMonitoring;
    setYieldEstimationDrawerIsOpen: (bool: boolean) => void;
    showHarvestRequest: boolean;
}) => {
    const units = useUnitProvider();

    const [reestimateModalIsOpen, setReestimateModalIsOpen] = useState(false);
    const appInstance = useSelector(getAppInstance);
    const cropData = getCropMonitoringData(cropMonitoring);
    const isHarvestDatePast = new Date(cropData?.expectedHarvestDate) < new Date();

    const fieldQuery = useFieldById({ id: cropMonitoring.field });

    const [selectedOption, setSelectedOption] = useState<string>();
    const { open: createReportModalIsOpen, toggle: toggleCreateReportModalIsOpen } = useToggle(false);
    const { open: showErrorModal, setOpen: setShowErrorModal, setClose: setCloseShowErrorModal } = useToggle(false);
    const canEditResource = useHasWriteAccess();
    return (
        <CropMonitoringDetailCard title={_('Preparate para tu cosecha')}>
            <Grid2 container spacing={2} sx={{ mt: 2 }}>
                {cropData?.yieldEstimation?.status === 'saved' ? (
                    <Grid2 item xs={12} sm={12}>
                        <Stack direction={'row'} p={4} py={1.5} justifyContent={'space-between'} alignItems={'center'}>
                            <Stack>
                                <Typography
                                    color='primary'
                                    variant='subtitle1'
                                    sx={{
                                        fontWeight: 'bold',
                                    }}
                                >
                                    {_('Rendimiento estimado de tu cultivo')}
                                </Typography>
                                <Typography
                                    color={'secondary'}
                                    sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                                    variant='body2'
                                    onClick={() => {
                                        setReestimateModalIsOpen(true);
                                    }}
                                >
                                    {_('Volver a estimar')}
                                </Typography>
                            </Stack>
                            <Typography color='primary'>
                                {units.toWeightPerArea(Number(cropData?.yieldEstimation.result) || 0, 2).toString()}
                            </Typography>
                        </Stack>
                    </Grid2>
                ) : (
                    <Grid2 item container px={4} columnSpacing={2} alignItems={'center'}>
                        <Grid2 xs={12} sm={8}>
                            <Stack>
                                <Typography
                                    color='primary'
                                    variant='subtitle1'
                                    sx={{
                                        fontWeight: 'bold',
                                    }}
                                >
                                    {_('Estimá el rendimiento de tu cultivo')}
                                </Typography>
                                <Typography color='primary' variant='body2'>
                                    {_('Tomá una o más muestras de tu cultivo y obtené un rendimiento estimado.')}
                                </Typography>
                            </Stack>
                        </Grid2>

                        <Grid2 xs={12} sm={4} sx={{ textAlign: 'end' }}>
                            <Button
                                variant='text'
                                color='primary'
                                sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                                align='right'
                                onClick={() => {
                                    setYieldEstimationDrawerIsOpen(true);
                                }}
                                disabled={!canEditResource}
                            >
                                {_('Estimar rendimiento')}
                            </Button>
                        </Grid2>
                    </Grid2>
                )}
                {cropData?.expectedHarvestDate && !isHarvestDatePast && (
                    <Grid2
                        container
                        px={4}
                        py={1.5}
                        columnSpacing={2}
                        alignItems={'center'}
                        justifyContent={'space-between'}
                    >
                        <Grid2 xs={12} sm={8}>
                            <Stack>
                                <Typography
                                    color='primary'
                                    variant='subtitle1'
                                    sx={{
                                        fontWeight: 'bold',
                                    }}
                                >
                                    {_('Fecha probable de cosecha')}
                                </Typography>
                                <Typography color='primary' variant='body2'>
                                    {_('Calculada a partir de que el cultivo alcanza un 14,5% de humedad estable.')}
                                </Typography>
                            </Stack>
                        </Grid2>
                        <Grid2 xs={12} sm={4}>
                            <Typography color={'primary'} variant='body2' align='right'>
                                {formatDate(new Date(cropData?.expectedHarvestDate))}
                            </Typography>
                        </Grid2>
                    </Grid2>
                )}
                {cropData?.expectedHarvestDate && isHarvestDatePast && appInstance.enableCropMonitoringReport && (
                    <Grid2
                        container
                        px={4}
                        py={1.5}
                        columnSpacing={2}
                        alignItems={'center'}
                        justifyContent={'space-between'}
                    >
                        <Grid2 xs={12} sm={8}>
                            <Stack>
                                <Typography
                                    color='primary'
                                    variant='subtitle1'
                                    sx={{
                                        fontWeight: 'bold',
                                    }}
                                >
                                    {_('Tu cultivo alcanzó el % de humedad para ser cosechado')}
                                </Typography>
                                <Typography color='primary' variant='body2'>
                                    {_(
                                        'Si ya realizaste la cosecha, cargá los datos y accedé a un reporte detallado con los resultados de tu campaña.'
                                    )}
                                </Typography>
                            </Stack>
                        </Grid2>
                        <Grid2 xs={12} sm={4} sx={{ textAlign: 'end' }}>
                            <Button
                                variant='text'
                                color='primary'
                                sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                                align='right'
                                onClick={() => {
                                    toggleCreateReportModalIsOpen();
                                }}
                                disabled={!canEditResource}
                            >
                                {_('Cargar datos')}
                            </Button>
                        </Grid2>
                    </Grid2>
                )}
                {showHarvestRequest && (
                    <Grid2 xs={12} sm={12}>
                        <Stack
                            direction={'row'}
                            justifyContent={'space-between'}
                            spacing={1}
                            sx={{ p: 2, mx: 2, backgroundColor: colors.gray[200] }}
                        >
                            <Stack direction={'row'} alignItems={'center'} spacing={1}>
                                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                                <Typography color={'primary'} variant='caption' align='left'>
                                    {_('¿Ya cosechaste? Cargá los datos para ver el reporte.')}
                                </Typography>
                            </Stack>
                            <Button
                                variant='text'
                                color='primary'
                                sx={{ whiteSpace: 'nowrap', fontSize: 12, textDecoration: 'underline' }}
                                size='small'
                                onClick={() => {
                                    toggleCreateReportModalIsOpen();
                                }}
                                disabled={!canEditResource}
                            >
                                {_('Cargar datos')}
                            </Button>
                        </Stack>
                    </Grid2>
                )}
            </Grid2>
            <ModalV2
                open={reestimateModalIsOpen}
                onClose={() => {
                    setReestimateModalIsOpen(false);
                }}
                actions={
                    <>
                        <Button
                            variant='text'
                            color='secondary'
                            onClick={() => {
                                setReestimateModalIsOpen(false);
                            }}
                        >
                            {_('Cancelar')}
                        </Button>
                        <Button
                            variant='contained'
                            color='secondary'
                            onClick={() => {
                                setReestimateModalIsOpen(false);
                                setYieldEstimationDrawerIsOpen(true);
                            }}
                        >
                            {_('Nueva estimación')}
                        </Button>
                    </>
                }
                title={_('¿Querés hacer una nueva estimación?')}
                actionsProps={{ sx: { justifyContent: 'end' } }}
                maxWidth={'sm'}
            >
                <Stack justifyContent={'center'} alignItems={'center'}>
                    <Typography color={'primary'} variant='body1'>
                        {_('Si continuás, los datos cargados se perderán.')}
                    </Typography>
                </Stack>
            </ModalV2>
            <HarvestLabelAndModal
                field={fieldQuery.data}
                cropMonitoring={cropMonitoring}
                createReportModalIsOpen={createReportModalIsOpen}
                setCreateReportModalIsOpen={toggleCreateReportModalIsOpen}
                selectedOption={selectedOption}
                setSelectedOption={setSelectedOption}
                showErrorModal={showErrorModal}
                showAlert={false}
                automaticOpenModals={false}
            />
            {cropMonitoring && fieldQuery.data && (
                <CreateReportModal
                    field={fieldQuery.data}
                    cropMonitoring={cropMonitoring}
                    isOpen={createReportModalIsOpen}
                    setIsOpen={toggleCreateReportModalIsOpen}
                    type={selectedOption}
                    setShowErrorModal={setShowErrorModal}
                    showErrorModal={showErrorModal}
                />
            )}
        </CropMonitoringDetailCard>
    );
};

export default HarvestPreparationCard;
